import { ensureProtectedCharacterTags } from './canonicalCharacterTags'
import { ensureProtectedDiscussedTag } from './canonicalDiscussedTag'
import { ensureProtectedElementsTags } from './canonicalElementsTags'
import { ensureProtectedMomentTag } from './canonicalMomentTag'
import { ensureProtectedPinnedTag } from './canonicalPinnedTag'
import { ensureProtectedRatingTags } from './canonicalRatingTags'

export type TagKind = 'category' | 'rating'

export interface TagDefinition {
  name: string
  parentIds?: string[]
  description?: string
  color?: string
  hidden?: boolean
  order?: number
  /** Semantic kind — 'rating' participates in ordinal scales; defaults to 'category' when omitted. */
  kind?: TagKind
  /** For rating tags: the dimension being rated (e.g. 'love', 'readiness'). */
  dimension?: string
  /** For rating tags: the scale value (1..N). Absent on family-root rating tags. */
  ordinal?: number
}

export interface TagRegistry {
  version: number
  tags: Record<string, TagDefinition>
}

/** Apply every protected-family guarantee in one place (characters, ratings, moment, elements, pinned). */
function ensureProtectedFamilies(registry: TagRegistry): TagRegistry {
  return ensureProtectedDiscussedTag(
    ensureProtectedPinnedTag(
      ensureProtectedElementsTags(
        ensureProtectedMomentTag(ensureProtectedRatingTags(ensureProtectedCharacterTags(registry))),
      ),
    ),
  )
}

export interface TagTreeNode {
  id: string
  name: string
  isTag: boolean
  parentIds: string[]
  hidden: boolean
  order: number
  children: TagTreeNode[]
}

interface LegacyTagMeta {
  description?: string
  color?: string
}

interface LegacyTagRegistry {
  version?: number
  tags?: Record<string, LegacyTagMeta>
}

export function createStableTagId(seed: string): string {
  let hash = 2166136261
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return `tag_${hash.toString(36)}`
}

export function createTagId(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_\-\u0590-\u05ff]+/g, '')
    .slice(0, 24)
  const suffix = Math.random().toString(36).slice(2, 8)
  return `tag_${normalized || 'item'}_${suffix}`
}

function isModernTagDefinition(value: unknown): value is TagDefinition {
  if (!value || typeof value !== 'object') {
    return false
  }
  const maybe = value as TagDefinition
  return typeof maybe.name === 'string'
}

export function migrateLegacyTagRegistry(input: LegacyTagRegistry): TagRegistry {
  const legacyTags = input.tags || {}
  const paths = Object.keys(legacyTags).sort((a, b) => {
    const depthDiff = a.split('/').length - b.split('/').length
    return depthDiff !== 0 ? depthDiff : a.localeCompare(b, 'he')
  })

  const pathToId = new Map<string, string>()
  const tags: Record<string, TagDefinition> = {}

  for (const path of paths) {
    const id = createStableTagId(path)
    pathToId.set(path, id)
  }

  for (const path of paths) {
    const id = pathToId.get(path)!
    const meta = legacyTags[path] || {}
    const parts = path.split('/').filter(Boolean)
    const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : null
    tags[id] = {
      name: parts[parts.length - 1] || path,
      parentIds: parentPath ? [pathToId.get(parentPath)!] : [],
      ...(meta.description ? { description: meta.description } : {}),
      ...(meta.color ? { color: meta.color } : {}),
    }
  }

  return ensureProtectedFamilies({ version: 2, tags })
}

export function normalizeTagRegistry(input: unknown): TagRegistry {
  if (!input || typeof input !== 'object') {
    return ensureProtectedFamilies({ version: 2, tags: {} })
  }

  const maybeRegistry = input as { version?: number; tags?: Record<string, unknown> }
  const rawTags = maybeRegistry.tags || {}
  const entries = Object.entries(rawTags)

  if (entries.length === 0) {
    return ensureProtectedFamilies({ version: 2, tags: {} })
  }

  if (entries.every(([, value]) => isModernTagDefinition(value))) {
    const tags: Record<string, TagDefinition> = {}
    const modernEntries = entries as Array<[string, TagDefinition]>
    for (const [id, value] of modernEntries) {
      tags[id] = {
        name: value.name.trim() || id,
        parentIds: Array.isArray(value.parentIds)
          ? [...new Set(value.parentIds.filter((parentId): parentId is string => typeof parentId === 'string'))]
          : [],
        ...(value.description ? { description: value.description } : {}),
        ...(value.color ? { color: value.color } : {}),
        ...(value.hidden ? { hidden: true } : {}),
        ...(typeof value.order === 'number' ? { order: value.order } : {}),
        ...(value.kind === 'rating' || value.kind === 'category' ? { kind: value.kind } : {}),
        ...(typeof value.dimension === 'string' ? { dimension: value.dimension } : {}),
        ...(typeof value.ordinal === 'number' ? { ordinal: value.ordinal } : {}),
      }
    }
    return ensureProtectedFamilies({
      version: Math.max(2, maybeRegistry.version || 2),
      tags,
    })
  }

  return migrateLegacyTagRegistry(maybeRegistry as LegacyTagRegistry)
}

export function buildTagTree(tags: Record<string, TagDefinition>): TagTreeNode[] {
  const nodes = new Map<string, TagTreeNode>()

  for (const [id, tag] of Object.entries(tags)) {
    nodes.set(id, {
      id,
      name: tag.name,
      isTag: true,
      parentIds: tag.parentIds || [],
      hidden: Boolean(tag.hidden),
      order: typeof tag.order === 'number' ? tag.order : 0,
      children: [],
    })
  }

  const roots: TagTreeNode[] = []
  for (const node of nodes.values()) {
    const validParents = node.parentIds.filter((parentId) => nodes.has(parentId))
    if (validParents.length === 0) {
      roots.push(node)
      continue
    }
    for (const parentId of validParents) {
      nodes.get(parentId)!.children.push(node)
    }
  }

  const sortNodes = (items: TagTreeNode[]) => {
    items.sort((a, b) => {
      const orderDiff = a.order - b.order
      return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name, 'he')
    })
    for (const item of items) {
      const seen = new Set<string>()
      item.children = item.children.filter((child) => {
        if (seen.has(child.id)) {
          return false
        }
        seen.add(child.id)
        return true
      })
      sortNodes(item.children)
    }
  }

  sortNodes(roots)
  return roots
}

/**
 * Every id in `rootId`'s subtree, including `rootId` itself.
 *
 * Indexes parent → children once up front rather than rescanning the whole
 * registry for each node visited. Callers run this per tree node — `getNodeCount`
 * does, once for every row the tag tree renders — so the rescan made a single
 * render quadratic in the registry and, at ~750 tags, cost hundreds of
 * milliseconds of blocked main thread.
 */
export function collectDescendantIds(tags: Record<string, TagDefinition>, rootId: string): Set<string> {
  const childIdsByParent = new Map<string, string[]>()
  for (const [id, tag] of Object.entries(tags)) {
    for (const parentId of tag.parentIds || []) {
      const siblings = childIdsByParent.get(parentId)
      if (siblings) {
        siblings.push(id)
      } else {
        childIdsByParent.set(parentId, [id])
      }
    }
  }

  const descendants = new Set<string>()
  const stack = [rootId]

  while (stack.length > 0) {
    const current = stack.pop()!
    if (descendants.has(current)) {
      continue
    }
    descendants.add(current)
    for (const childId of childIdsByParent.get(current) || []) {
      stack.push(childId)
    }
  }

  return descendants
}

export function getTagDisplayPath(tags: Record<string, TagDefinition>, tagId: string): string {
  const tag = tags[tagId]
  if (!tag) {
    return tagId
  }

  const seen = new Set<string>()
  const parts: string[] = []
  let currentId: string | undefined = tagId

  while (currentId && !seen.has(currentId)) {
    seen.add(currentId)
    const current: TagDefinition | undefined = tags[currentId]
    if (!current) {
      break
    }
    parts.unshift(current.name)
    currentId = current.parentIds?.[0]
  }

  return parts.join('/')
}

export function getTagDisplayName(tags: Record<string, TagDefinition>, tagId: string): string {
  return tags[tagId]?.name || tagId
}

export function collectHiddenIds(tags: Record<string, TagDefinition>): Set<string> {
  const hiddenIds = new Set<string>()
  for (const [id, tag] of Object.entries(tags)) {
    if (tag.hidden) {
      for (const descendantId of collectDescendantIds(tags, id)) {
        hiddenIds.add(descendantId)
      }
    }
  }
  return hiddenIds
}

export function getTagDisplayParts(
  tags: Record<string, TagDefinition>,
  tagId: string,
): {
  category: string | null
  leaf: string
} {
  const path = getTagDisplayPath(tags, tagId)
  const idx = path.lastIndexOf('/')
  return idx === -1 ? { category: null, leaf: path } : { category: path.slice(0, idx), leaf: path.slice(idx + 1) }
}

/**
 * Convert a hex color (#rgb / #rrggbb) to an rgba() string with the given alpha.
 * Returns null for unparseable input so callers can decide on a fallback.
 */
export function hexToRgba(hex: string, alpha: number): string | null {
  if (!hex || hex[0] !== '#') {
    return null
  }
  let body = hex.slice(1)
  if (body.length === 3) {
    body = body
      .split('')
      .map((ch) => ch + ch)
      .join('')
  }
  if (body.length !== 6 || !/^[0-9a-f]{6}$/i.test(body)) {
    return null
  }
  const r = parseInt(body.slice(0, 2), 16)
  const g = parseInt(body.slice(2, 4), 16)
  const b = parseInt(body.slice(4, 6), 16)
  const a = Math.max(0, Math.min(1, alpha))
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

export function resolveTagColor(
  tagId: string,
  colorMap: Record<string, string>,
  tags: Record<string, TagDefinition>,
): string {
  const DEFAULT = '#9e9e9e'
  const stack = [tagId]
  const visited = new Set<string>()

  while (stack.length > 0) {
    const current = stack.shift()!
    if (visited.has(current)) {
      continue
    }
    visited.add(current)
    if (colorMap[current]) {
      return colorMap[current]
    }
    const parents = tags[current]?.parentIds || []
    stack.push(...parents)
  }

  return DEFAULT
}

/**
 * Tags that exist in both registries, are parented in `current`, and arrive orphaned.
 *
 * The registry is written whole on every save, so one bad client payload can flatten the
 * entire hierarchy at once — which is exactly what happened on 2026-08-08, silently
 * detaching 730 of 740 parented tags and emptying every hierarchy-driven view. Callers
 * use this to reject a wholesale flattening while letting ordinary re-parenting through.
 */
export function findNewlyOrphanedTags(
  current: Record<string, TagDefinition>,
  incoming: Record<string, TagDefinition>,
): string[] {
  const out: string[] = []
  for (const [id, def] of Object.entries(current)) {
    if ((def.parentIds || []).length === 0) {
      continue
    }
    const next = incoming[id]
    if (next && (next.parentIds || []).length === 0) {
      out.push(id)
    }
  }
  return out
}

export function getNodeCount(
  tagId: string,
  counts: Record<string, number>,
  tags: Record<string, TagDefinition>,
): number {
  let total = 0
  for (const id of collectDescendantIds(tags, tagId)) {
    total += counts[id] || 0
  }
  return total
}
